<article (focusin)="onDetailFocus($event)" class="pdw" aria-label="Well exception findings">
  <header [class.pdw-head--with-summary]="!!payload()" class="pdw-head">
    <div class="pdw-head__main">
      <div class="pdw-head__title-row">
        <span class="pdw-head__kind">Well exception</span>
        <span class="pdw-head__state">Detected</span>
      </div>
      <h1 class="pdw-head__title">{{ title() }}</h1>
      <p class="pdw-head__sub">{{ facility() }}@if (payload(); as result) { · {{ wellCount() }} {{ wellCount() === 1 ? 'well' : 'wells' }} · {{ result.dossier.signals.length }} {{ result.dossier.signals.length === 1 ? 'signal' : 'signals' }} }</p>
    </div>
    @if (payload(); as result) {
      <div class="detail-header-summary">
        @if (result.summary.trim().length > 0) {
          <div class="detail-header-summary__text">
            <app-console-summary-markdown [text]="result.summary" />
          </div>
        } @else {
          <p class="detail-header-summary__text detail-header-summary__text--empty">No concern summary available.</p>
        }
        <hr class="detail-header-summary__divider" />
      </div>
    }
    <dl class="pdw-head__facts" aria-label="Well exception details">
      <div><dt>Agent Case ID</dt><dd class="detail-case-id__value">{{ header().agentCaseId }}</dd></div>
      @if (payload(); as result) {
        <div><dt>First observed</dt><dd>{{ (result.dossier.eventOnsetUtc ?? result.dossier.reconstructedOnsetUtc) | date:dateFormat:'+0100' }} WAT</dd></div>
        <div><dt>Detected</dt><dd>{{ result.dossier.detectedUtc | date:dateFormat:'+0100' }} WAT</dd></div>
        <div><dt>{{ hasPressureEvents() ? 'Detector latency' : 'Detection latency' }}</dt><dd>{{ result.dossier.detectionLatencyHours | number:'1.0-2' }} hours</dd></div>
        <div><dt>Last analysis</dt><dd>{{ header().sweepTimeUtc | date:dateFormat:'+0100' }} WAT</dd></div>
      }
    </dl>
    <div class="pdw-head__actions">
      <ng-content select="[headerFeedback]" />
      @if (!localPreview() && networkAssetCodes().length) {
        <button
          #networkContextTrigger
          [attr.aria-expanded]="networkBladeOpen()"
          (click)="onNetworkContextOpened()"
          type="button"
          class="network-context-button"
          aria-controls="uc2-network-context">
          <i class="pi pi-sitemap" aria-hidden="true"></i>
          <span>Network context</span>
        </button>
      }
    </div>
  </header>

  <nav class="workflow-header" role="tablist" aria-label="Well exception detail">
    @for (tab of tabIds(); track tab) {
      <button
        [id]="'uc2-tab-' + tab"
        [attr.aria-controls]="'uc2-panel-' + tab"
        [attr.aria-selected]="activeTab() === tab"
        [attr.aria-label]="tab === 'workflow' && workflowTabBadge() ? tabLabels[tab] + ', ' + workflowTabBadge() : tabLabels[tab]"
        [tabIndex]="activeTab() === tab ? 0 : -1"
        [class.workflow-header__item--active]="activeTab() === tab"
        (click)="selectTab(tab)"
        (keydown)="onTabKeydown($event, tab)"
        type="button"
        role="tab"
        class="workflow-header__item">
        <span>{{ tabLabels[tab] }}</span>
        @if (tab === 'workflow' && workflowTabBadge(); as badge) {
          <span class="workflow-header__badge">{{ badge }}</span>
        }
      </button>
    }
  </nav>

  <section [hidden]="activeTab() !== 'workflow' || !hasWorkflow()"
    id="uc2-panel-workflow" role="tabpanel" aria-labelledby="uc2-tab-workflow" class="uc2-tab-panel">
    <ng-content select="[engineWorkflow]" />
  </section>

  <section [hidden]="activeTab() !== 'comments'"
    id="uc2-panel-comments" role="tabpanel" aria-labelledby="uc2-tab-comments" class="pdw-panel uc2-tab-panel">
    <div class="uc2-panel-header"><h2 class="uc2-panel-title">Comments</h2></div>
    <ng-content select="[caseComments]" />
  </section>

  @if (error()) {
    <p class="notice error" role="alert">{{ error() }}</p>
  } @else if (payload(); as result) {
    @let dossier = result.dossier;
    <section [hidden]="activeTab() !== 'evidence'" id="uc2-panel-evidence" role="tabpanel"
      aria-labelledby="uc2-tab-evidence" class="pdw-panel uc2-tab-panel" aria-label="Signal timeline">
      <div class="uc2-panel-header"><h2 class="uc2-panel-title">Signal timeline</h2></div>
        <p class="evidence__sub">@if (hasPressureEvents()) {
          One row per well, ordered by initial observed departure. Bars include the complete observed anomaly, including shifts after settling. Gaps separate returned-to-reference events. Overlapping onset brackets do not establish physical order. Settling is not recovery.
        } @else {
          Signals are ordered by their first observed change. Gaps separate distinct episodes. Order alone does not establish cause.
        } Select a well or signal to inspect its measurements.</p>
        <app-uc2-onset-timeline [rows]="timelineRows()" [selectedMemberId]="'' + selectedSignalIndex()"
          (memberSelected)="selectTimelineSignal($event)" />
        <div class="signal-picker" aria-label="Select a pressure signal">
          @for (row of timelineRows(); track row.exceptionId) {
            <button type="button" [attr.aria-pressed]="'' + selectedSignalIndex() === row.exceptionId || row.memberIds?.includes('' + selectedSignalIndex())"
              (click)="selectTimelineSignal(row.exceptionId)">{{ row.asset }}</button>
          }
        </div>
        @if (hasPressureEvents()) {
          <div class="signal-picker" aria-label="Select a measurement for this well">
            @for (signal of eventSignals(); track signal.signalId) {
              <button type="button" [attr.aria-pressed]="selectedSignal()?.signalId === signal.signalId"
                (click)="selectTimelineSignal('' + result.dossier.signals.indexOf(signal))">{{ signalLabel(signal) }}</button>
            }
          </div>
        }
        @if (selectedSignal(); as selected) {
          @for (event of selectedWellEvents(); track event.eventId) {
            <div class="evidence__signal-head">
              <h3 class="evidence__title">{{ selected.context.well_code }}: {{ event.pressureRole }} event</h3>
              <p class="evidence__sub">
                Onset bracket: {{ event.onsetNotBefore | date:dateFormat:'+0100' }} – {{ event.onset | date:dateFormat:'+0100' }} WAT.
                @if (event.settledAt) {
                  Sampled settling: {{ event.settledAt | date:dateFormat:'+0100' }} WAT;
                  confirmed {{ event.settlingConfirmedAt | date:dateFormat:'+0100' }} WAT.
                } @else { Settling not established. }
                @if (event.returnedToReferenceAt) { Return to reference: {{ event.returnedToReferenceAt | date:dateFormat:'+0100' }} WAT. }
                @else { Return to reference not observed. }
                @if (event.state === 'coverage-uncertain') { Continuation uncertain after coverage loss. }
                Observed through {{ event.observedThrough | date:dateFormat:'+0100' }} WAT. Settling is not recovery.
              </p>
              <ul class="evidence__flags">
                @for (comparison of event.channelComparisons; track comparison.signalId) {
                  <li>{{ comparison.pressureRole }}:
                    @if (comparison.direction === 'unassessed') { unassessed }
                    @else {
                      {{ comparison.preEventMedian | number:'1.0-2' }} → {{ comparison.postEventMedian | number:'1.0-2' }}
                      ({{ comparison.direction }}; change {{ comparison.delta | number:'1.0-2' }})
                    }
                  </li>
                }
              </ul>
            </div>
          }
          <div class="evidence__signal-head">
            <h3 class="evidence__title">{{ signalLabel(selected) }}</h3>
            <p class="evidence__sub">Analysis period: {{ selected.metrics.windowStartUtc | date:dateFormat:'+0100' }} – {{ selected.metrics.windowEndUtc | date:dateFormat:'+0100' }} WAT</p>
          </div>
          <dl class="evidence__metrics">
            <div><dt>Latest pressure</dt><dd>{{ selected.latestValue.value | number:'1.0-2' }} {{ selected.latestValue.uom }}</dd></div>
            <div><dt>Baseline pressure</dt><dd>{{ selected.metrics.baselineMedian | number:'1.0-2' }} {{ selected.metrics.units }}</dd></div>
            <div><dt>Change from baseline</dt><dd>{{ selected.metrics.deviationPsi | number:'1.0-2' }} {{ selected.metrics.units }}</dd></div>
            <div><dt>Deviation score</dt><dd>{{ selected.metrics.modifiedZ | number:'1.0-2' }}<small>Compared with normal baseline variability</small></dd></div>
            <div><dt>Persistence</dt><dd>{{ selected.metrics.persistenceSamples }} consecutive readings</dd></div>
            <div><dt>Detector episode onset</dt><dd>{{ selected.episodeOnsetUtc | date:dateFormat:'+0100' }} WAT</dd></div>
            <div><dt>Persistence confirmed</dt><dd>{{ selected.persistenceConfirmedAt | date:dateFormat:'+0100' }} WAT</dd></div>
            <div><dt>Latest reading</dt><dd>{{ selected.latestValue.timestamp | date:dateFormat:'+0100' }} WAT</dd></div>
          </dl>
          <p>{{ selected.signalDescription }}</p>
          @if (selected.state; as state) {
            <p class="evidence__sub">Operating state: {{ state.statusName ?? 'Not established' }}@if (state.effectiveDay) {, recorded {{ state.effectiveDay | date:'dd MMM yyyy':'+0100' }} WAT }.
              This is a daily record, not confirmation of the state at the event time.@if (state.ambiguous) { Available records are ambiguous. }</p>
          }
          @if (selected.dataQualityFlags.length) {
            <ul class="evidence__flags">@for (flag of selected.dataQualityFlags; track $index) { <li>{{ qualityNotice(flag) }}</li> }</ul>
          }
          <details class="measurement-details">
            <summary>Measurement statistics and detection thresholds</summary>
            <p class="evidence__sub">These values explain why the pressure change was detected; they do not establish its physical cause.</p>
            <div class="metric-tables">
              <div><h4>Observed statistics</h4><table>
                <thead><tr><th scope="col">Measurement</th><th scope="col">Value</th></tr></thead>
                <tbody>@for (metric of metricRows(); track $index) { <tr><th scope="row">{{ metric.label }}</th><td>{{ metric.value }}</td></tr> }</tbody>
              </table></div>
              <div><h4>Detection thresholds</h4><table>
                <thead><tr><th scope="col">Setting</th><th scope="col">Value</th></tr></thead>
                <tbody>@for (metric of parameterRows(); track $index) { <tr><th scope="row">{{ metric.label }}</th><td>{{ metric.value }}</td></tr> }</tbody>
              </table></div>
            </div>
            <h4>Pressure episodes for this measurement</h4>
            <div class="table-scroll"><table>
              <thead><tr><th scope="col">First observed (WAT)</th><th scope="col">Confirmed (WAT)</th><th scope="col">Last qualifying reading (WAT)</th><th scope="col">Qualifying readings</th><th scope="col">Baseline</th><th scope="col">Baseline variability</th><th scope="col">Required change</th></tr></thead>
              <tbody>@for (episode of selected.episodes; track $index) {
                <tr><td>{{ episode.onset | date:dateFormat:'+0100' }}</td><td>{{ episode.persistenceConfirmedAt | date:dateFormat:'+0100' }}</td><td>{{ episode.lastQualifyingAt | date:dateFormat:'+0100' }}</td><td>{{ episode.qualifyingSampleCount }}</td><td>{{ episode.baselineMedian | number:'1.0-2' }} {{ selected.metrics.units }}</td><td>{{ episode.baselineMad | number:'1.0-2' }} {{ selected.metrics.units }}</td><td>{{ episode.effectiveMagnitudeThreshold | number:'1.0-2' }} {{ selected.metrics.units }}</td></tr>
              }</tbody>
            </table></div>
          </details>
        }
    </section>

    <section [hidden]="activeTab() !== 'trends'" id="uc2-panel-trends" role="tabpanel"
      aria-labelledby="uc2-tab-trends" class="pdw-panel uc2-tab-panel" aria-label="Telemetry by signal">
      <div class="uc2-panel-header"><h2 class="uc2-panel-title">Pressure trends</h2></div>
      @if (activeTab() === 'trends') {
        <p class="evidence__sub telemetry-note">All chart times are West Africa Time (WAT). Supporting measurements provide context, not a separate diagnosis.</p>
        @if (!hasPressureEvents()) {
          <p class="evidence__sub">Complete anomaly timing and settling were not recorded in this result. The marker uses the recorded first observed change; no overall transient is inferred.</p>
        }
        <app-uc2-pressure-trends [graphs]="eventGraphs()" [period]="eventPeriod()"
          [firstObservedAt]="result.dossier.reconstructedOnsetUtc" />
      }
    </section>

    @if (activeTab() === 'trends') {
      <details class="pdw-more telemetry-originals">
        <summary class="pdw-more__summary pdw-more__summary--disclosure">Individual charts</summary>
        <div class="pdw-more__body">
        @for (entry of visibleTelemetry(); track $index) {
          <article class="signal-telemetry" [attr.aria-label]="entry.label">
            <h3 class="evidence__title">{{ entry.label }}</h3>
            <div class="telemetry-grid">
              @for (chart of entry.charts; track $index) {
                <div class="telemetry-chart"><app-reusable-chart [graph]="chart.graph" [firstObservedAt]="eventPeriod()?.onset ?? result.dossier.reconstructedOnsetUtc" [eventPeriod]="eventPeriod()" height="20rem" /></div>
              }
            </div>
            @for (notice of entry.emptySources; track $index) { <p class="evidence__sub">{{ notice }}</p> }
            @if (!entry.charts.length) { <p role="status">No charts to display for this measurement.</p> }
          </article>
        }
        </div>
      </details>
    }

    <section [hidden]="activeTab() !== 'quality'" id="uc2-panel-quality" role="tabpanel"
      aria-labelledby="uc2-tab-quality" class="pdw-panel uc2-tab-panel" aria-label="Data quality and limitations">
      <div class="uc2-panel-header"><h2 class="uc2-panel-title">Data quality and limitations</h2></div>
          @for (signal of dossier.signals; track signal.signalId) {
            @if (signal.dataQualityFlags.length) {
              <h3 class="evidence__title">{{ signalLabel(signal) }}</h3>
              <ul>@for (flag of signal.dataQualityFlags; track $index) { <li>{{ qualityNotice(flag) }}</li> }</ul>
            }
          }
          @if (dossier.limitations.length) {
          <ul>@for (limitation of dossier.limitations; track $index) { <li>{{ limitation }}</li> }</ul>
          } @else {
            <p>No additional limitations were supplied.</p>
          }
    </section>
  } @else {
    <p role="status">Loading the well exception findings...</p>
  }
</article>

@if (!localPreview()) {
<app-uc2-network-blade
  [open]="networkBladeOpen()"
  [wellCode]="selectedSignal()?.context?.well_code ?? null"
  [assetCodes]="networkAssetCodes()"
  (closed)="onNetworkContextClosed()" />
}
